/**
 * v3.15-1125 — Send To Partner block (company-configurable delivery).
 *
 * Rendered on the BF Application card. Reads the deal's company config:
 *   - no method configured → grayed-out hint
 *   - method configured    → cyan "Send To Partner" button
 * After click: hits POST /clients/{id}/send-to-partner, displays the
 * result, and refreshes the parent dashboard via onSuccess.
 *
 * Shows audit summary (last sent timestamp + method + status) when the
 * client has a prior send recorded.
 */
import React, { useState } from 'react';
import { Send, Loader2, AlertCircle, CheckCircle2, Settings } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const METHOD_LABEL = {
  api:    'API',
  email:  'Email',
  manual: 'Manual',
};

export default function SendToPartnerBlock({ client, company, onSuccess }) {
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const method = (company?.partner_delivery_config?.method || '').toLowerCase();
  const configured = ['api', 'email', 'manual'].includes(method);
  const companyName = company?.name || 'this company';

  const lastSentAt = client?.partner_sent_to_partner_at;
  const lastSentBy = client?.partner_sent_to_partner_by;
  const lastStatus = client?.partner_delivery_status;
  const lastMethod = client?.partner_delivery_method_used;
  const lastRespStatus = client?.partner_delivery_response_status;

  const handleSend = async () => {
    if (!client?.id) return;
    if (!configured) return;
    setSending(true);
    setLastResult(null);
    try {
      const { data } = await api.post(`/clients/${client.id}/send-to-partner`);
      setLastResult(data);
      if (data?.status === 'ok') {
        toast.success(
          method === 'manual'
            ? 'Recorded manual submission'
            : `Sent to ${companyName} via ${METHOD_LABEL[data.method] || data.method}`
        );
        if (typeof onSuccess === 'function') onSuccess(data);
      } else {
        toast.error(data?.detail || 'Partner delivery failed');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Partner delivery failed';
      setLastResult({ status: 'failed', detail });
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-2 border-t" data-testid="send-to-partner-block">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Send className="h-3.5 w-3.5 text-cyan-700 shrink-0" />
          <p className="text-[10px] uppercase tracking-wide font-semibold text-cyan-800">
            Send To Partner
          </p>
          {configured ? (
            <span
              className="text-[10px] text-cyan-700 bg-cyan-50 border border-cyan-200 rounded px-1.5 py-0.5"
              data-testid="send-to-partner-method-badge"
            >
              {METHOD_LABEL[method]}
            </span>
          ) : (
            <span
              className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1"
              data-testid="send-to-partner-not-configured"
            >
              <Settings className="h-2.5 w-2.5" />
              Not configured
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handleSend}
          disabled={sending || !configured || !client?.id}
          className={`text-[11px] px-3 py-1 rounded font-medium transition-colors ${
            !configured
              ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
              : 'bg-cyan-600 hover:bg-cyan-700 text-white disabled:opacity-60'
          }`}
          title={
            configured
              ? `Send this application to ${companyName} via ${METHOD_LABEL[method]}`
              : 'Configure a Partner Delivery method on this company first (Edit Company → Partner Delivery)'
          }
          data-testid="send-to-partner-btn"
        >
          {sending ? (
            <><Loader2 className="h-3 w-3 animate-spin inline mr-1" /> Sending…</>
          ) : lastSentAt ? (
            'Re-send To Partner'
          ) : (
            'Send To Partner'
          )}
        </button>
      </div>

      {!configured && (
        <p className="text-[10px] text-slate-500 mt-1" data-testid="send-to-partner-config-hint">
          Open Edit Company → Partner Delivery to configure how this partner receives applications.
        </p>
      )}

      {lastSentAt && (
        <div
          className="mt-1.5 text-[10px] text-slate-600 flex items-center gap-2 flex-wrap"
          data-testid="send-to-partner-last-sent"
        >
          <CheckCircle2 className="h-3 w-3 text-emerald-600" />
          <span>
            Last sent: <span className="font-medium text-slate-800">{new Date(lastSentAt).toLocaleString()}</span>
            {lastSentBy ? <> · by <span className="text-slate-700">{lastSentBy}</span></> : null}
          </span>
          <span className="text-slate-400">·</span>
          <span>
            Method: <span className="text-slate-700">{METHOD_LABEL[lastMethod] || lastMethod || '—'}</span>
          </span>
          <span className="text-slate-400">·</span>
          <span>
            Status: <span className="text-emerald-700 font-medium">{lastStatus || '—'}</span>
            {lastRespStatus && lastStatus !== String(lastRespStatus) ? <> ({lastRespStatus})</> : null}
          </span>
        </div>
      )}

      {lastResult?.status === 'failed' && (
        <div
          className="mt-1.5 text-[10px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 flex items-start gap-1"
          data-testid="send-to-partner-error"
        >
          <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
          <span>{lastResult.detail || 'Send failed'}</span>
        </div>
      )}
    </div>
  );
}
